import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addAddress,
  addEmail,
  addPhone,
  createCustomer,
  deactivateAddress,
  deactivateCustomer,
  deactivateEmail,
  deactivatePhone,
  getCustomer,
  listCustomers,
  updateAddress,
  updateCustomerCore,
  updateEmail,
  updatePhone,
} from './api'
import type {
  AddressDto,
  CreateCustomerRequest,
  EmailDto,
  PhoneDto,
  UpdateCustomerCoreRequest,
} from './types'

export const customerKeys = {
  all: ['customers'] as const,
  list: (offset: number, limit: number) => ['customers', 'list', offset, limit] as const,
  detail: (id: number) => ['customers', 'detail', id] as const,
}

export function useCustomers(offset = 0, limit = 50) {
  return useQuery({
    queryKey: customerKeys.list(offset, limit),
    queryFn: () => listCustomers(offset, limit),
  })
}

export function useCustomer(id: number | null | undefined) {
  return useQuery({
    queryKey: customerKeys.detail(id ?? -1),
    queryFn: () => getCustomer(id as number),
    enabled: typeof id === 'number' && id > 0,
  })
}

function invalidateCustomers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: customerKeys.all })
}

function invalidateCustomer(qc: ReturnType<typeof useQueryClient>, id: number) {
  qc.invalidateQueries({ queryKey: customerKeys.detail(id) })
  qc.invalidateQueries({ queryKey: ['customers', 'list'] })
}

export function useCreateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateCustomerRequest) => createCustomer(payload),
    onSuccess: () => invalidateCustomers(qc),
  })
}

export function useUpdateCustomerCore(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateCustomerCoreRequest) => updateCustomerCore(id, payload),
    onSuccess: () => invalidateCustomer(qc, id),
  })
}

export function useDeactivateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deactivateCustomer(id),
    onSuccess: (_, id) => invalidateCustomer(qc, id),
  })
}

export function useAddPhone(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (phone: PhoneDto) => addPhone(customerId, phone),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useUpdatePhone(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ phoneId, phone }: { phoneId: number; phone: PhoneDto }) =>
      updatePhone(customerId, phoneId, phone),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useDeactivatePhone(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (phoneId: number) => deactivatePhone(customerId, phoneId),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useAddEmail(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (email: EmailDto) => addEmail(customerId, email),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useUpdateEmail(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId, email }: { emailId: number; email: EmailDto }) =>
      updateEmail(customerId, emailId, email),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useDeactivateEmail(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (emailId: number) => deactivateEmail(customerId, emailId),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useAddAddress(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (address: AddressDto) => addAddress(customerId, address),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useUpdateAddress(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ addressId, address }: { addressId: number; address: AddressDto }) =>
      updateAddress(customerId, addressId, address),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}

export function useDeactivateAddress(customerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (addressId: number) => deactivateAddress(customerId, addressId),
    onSuccess: () => invalidateCustomer(qc, customerId),
  })
}
